// coins.ts — the platform's RED, GREEN and GOLD coin marks, inline.
//
// Web Client keeps these as .svg files under src/currency/assets and lets Vite
// inline them; this console cannot. Its tests run under bare Node
// (`node --experimental-strip-types --test`), which has no asset pipeline and
// refuses an .svg import outright — so the art lives here as markup and becomes
// a data URI at module load. Vite emits the very same data URI for the file
// version, so the two forms are the same bytes on screen; only the authoring
// differs.
//
// The markup is kept readable (percent-encoded, not base64) so a change to the
// art shows up as a reviewable diff. It must stay byte-identical to Web
// Client's assets: SM-443's seed promise is that an unconfigured currency looks
// the SAME in Web Client, Backoffice and MM Web, and three drifting copies of a
// coin is exactly how that promise dies quietly.
//
// GOLD is here too, since SM-488. It had no entry while SM-334/SM-441 made its
// mark the ACTIVE brand's coin — a thing that differs per host and so could not
// be a platform constant. That fork is gone: one account holds one GOLD wallet
// described by one registry row, so GOLD's seeded mark is a platform constant
// exactly like the other two.
//
// SM-505 replaced all three coins with the artwork the product supplied. The
// markup below must stay byte-identical to Web Client's .svg files — the
// digest table in the parity suites is what enforces that — so a change to
// the art is a change to four files in one commit, never one.
//
// RED and GREEN were then swapped: the supplied files map by filename, but
// `default-currency-red.png` is green artwork and its green counterpart is
// teal, so seeding them as named put a green coin on RED. GREEN now holds
// the green coin and RED the teal one.

const RED_COIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="img">
  <circle cx="16" cy="16" r="14.5" fill="#68ddda"/>
  <g fill="none" stroke="#a6ede9" stroke-width="1.15" stroke-linecap="round">
    <path d="M28.89 15.55 A12.90 12.90 0 0 1 20.41 28.12"/>
    <path d="M4.94 22.64 A12.90 12.90 0 0 1 3.12 15.32"/>
    <path d="M4.94 9.36 A12.90 12.90 0 0 1 7.88 5.97"/>
    <path d="M9.16 5.06 A12.90 12.90 0 0 1 16.68 3.12"/>
  </g>
  <circle cx="16" cy="16" r="10.44" fill="#a6ede9" stroke="#43b5ac" stroke-width="1.875"/>
  <g fill="#43b5ac">
    <rect x="10.00" y="16.38" width="3.40" height="5.12" rx="0.35"/>
    <rect x="14.30" y="9.25" width="3.40" height="12.25" rx="0.35"/>
    <rect x="18.60" y="12.75" width="3.40" height="8.75" rx="0.35"/>
  </g>
</svg>`;

const GREEN_COIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="img">
  <circle cx="16" cy="16" r="14.5" fill="#5db756"/>
  <g fill="none" stroke="#73ce51" stroke-width="1.15" stroke-linecap="round">
    <path d="M28.89 15.55 A12.90 12.90 0 0 1 20.41 28.12"/>
    <path d="M4.94 22.64 A12.90 12.90 0 0 1 3.12 15.32"/>
    <path d="M4.94 9.36 A12.90 12.90 0 0 1 7.88 5.97"/>
    <path d="M9.16 5.06 A12.90 12.90 0 0 1 16.68 3.12"/>
  </g>
  <circle cx="16" cy="16" r="10.44" fill="#80d87d" stroke="#40963b" stroke-width="1.875"/>
  <g fill="#40963b">
    <rect x="10.00" y="16.38" width="3.40" height="5.12" rx="0.35"/>
    <rect x="14.30" y="12.75" width="3.40" height="8.75" rx="0.35"/>
    <rect x="18.60" y="9.25" width="3.40" height="12.25" rx="0.35"/>
  </g>
</svg>`;

const GOLD_COIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="img">
  <circle cx="16" cy="16" r="14.5" fill="#ffb703"/>
  <g fill="none" stroke="#ffcc29" stroke-width="1.15" stroke-linecap="round">
    <path d="M28.89 15.55 A12.90 12.90 0 0 1 20.41 28.12"/>
    <path d="M4.94 22.64 A12.90 12.90 0 0 1 3.12 15.32"/>
    <path d="M4.94 9.36 A12.90 12.90 0 0 1 7.88 5.97"/>
    <path d="M9.16 5.06 A12.90 12.90 0 0 1 16.68 3.12"/>
  </g>
  <circle cx="16" cy="16" r="10.44" fill="#ffcc29" stroke="#f99300" stroke-width="1.875"/>
  <g fill="#f99300">
    <path d="M9.3 13.1 L13.2 15.1 L16 10.9 L18.8 15.1 L22.7 13.1 L22.7 20.75 L9.3 20.75 Z" stroke="#f99300" stroke-width="0.45" stroke-linejoin="round"/>
  </g>
</svg>`;

const toDataUrl = (svg: string) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

export const coinRedUrl = toDataUrl(RED_COIN_SVG);
export const coinGreenUrl = toDataUrl(GREEN_COIN_SVG);
export const coinGoldUrl = toDataUrl(GOLD_COIN_SVG);
